import { useState } from "react";
import { FileDown, FileSpreadsheet, Printer, Mail } from "lucide-react";
import { Button } from "./ui/Button";
import { Input } from "./ui/Input";
import { Modal } from "./ui/Modal";
import type { Column } from "./ui/DataTable";
import { exportRowsToCSV, buildCsvText } from "../lib/exportCsv";
import { exportRowsToXlsx } from "../lib/exportXlsx";
import { printRowsAsPdf } from "../lib/printTable";
import api from "../lib/api";

interface TableExportToolbarProps<T> {
  rows: T[]; // huidige gefilterde+gesorteerde rijen (van onVisibleRowsChange)
  columns: Column<T>[];
  filename: string; // bijv. "offertes"
  source: string; // bijv. "offertes" — label in mail-onderwerp
  companyEmail?: string;
  // Indien gezet: PDF-knop haalt A4-template-HTML op per geselecteerde rij en
  // drukt af via een verborgen iframe (gebruikt de lay-out-template, geen window.print).
  previewKind?: "quotes" | "invoices" | "payments" | "expenses";
  /** Titel voor de tabel-PDF wanneer er geen previewKind is (bijv. dashboard-
   *  overzicht). Zonder previewKind print de PDF-knop anders window.print() van
   *  de hele pagina; mét deze titel print hij alleen de tabel via een iframe. */
  printTitle?: string;
}

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3300";

export function TableExportToolbar<T>({
  rows,
  columns,
  filename,
  source,
  companyEmail,
  previewKind,
  printTitle,
}: TableExportToolbarProps<T>) {
  const [mailOpen, setMailOpen] = useState(false);
  const [to, setTo] = useState(companyEmail || "");
  const [subject, setSubject] = useState(`AIBoek export — ${source}`);
  const [sending, setSending] = useState(false);
  const [mailError, setMailError] = useState("");
  const [mailOk, setMailOk] = useState("");
  const [pdfBusy, setPdfBusy] = useState(false);

  const handleCsv = () => {
    if (!rows.length) {
      alert("Er zijn geen rijen om te exporteren.");
      return;
    }
    exportRowsToCSV(rows, columns, filename);
  };

  const handleXlsx = () => {
    if (!rows.length) {
      alert("Er zijn geen rijen om te exporteren.");
      return;
    }
    exportRowsToXlsx(rows, columns, filename);
  };

  const handlePdf = async () => {
    if (!previewKind) {
      // Geen A4-preview-mode (bijv. dashboard-overzicht): print alleen de tabel
      // via een off-screen iframe → schone tabel-PDF zonder de rest van de pagina.
      if (!rows.length) {
        alert("Er zijn geen rijen om af te drukken.");
        return;
      }
      printRowsAsPdf(printTitle || source, rows, columns);
      return;
    }
    if (!rows.length) {
      alert("Er zijn geen rijen om af te drukken.");
      return;
    }
    const ids = rows.map((r: any) => r.id).filter(Boolean);
    if (!ids.length) {
      window.print();
      return;
    }
    setPdfBusy(true);
    try {
      const res = await api.post(`/api/${previewKind}/preview-bulk`, { ids }, {
        responseType: "text",
        transformResponse: (x) => x, // voorkom JSON-parsing van HTML
      });
      const html = typeof res.data === "string" ? res.data : String(res.data);
      printHtmlViaIframe(html);
    } catch (err) {
      console.error("Bulk preview failed", err);
      window.print();
    } finally {
      setPdfBusy(false);
    }
  };

  /** Schrijft HTML in een off-screen iframe met echte afmetingen (zodat
   *  getBoundingClientRect() in het REPOSITION_SCRIPT correct meet), wacht tot
   *  load + het reposition-script, en drukt pas daarna af. Relatieve
   *  /uploads-URLs werken via <base>. */
  function printHtmlViaIframe(html: string) {
    const existing = document.getElementById("aiboek-print-iframe") as HTMLIFrameElement | null;
    if (existing) existing.remove();
    const iframe = document.createElement("iframe");
    iframe.id = "aiboek-print-iframe";
    // Echte afmetingen off-screen: nodig voor correcte lay-out/hoogte-meting.
    iframe.style.position = "fixed";
    iframe.style.left = "-10000px";
    iframe.style.top = "0";
    iframe.style.width = "820px";
    iframe.style.height = "1180px";
    iframe.style.border = "0";
    document.body.appendChild(iframe);

    const win = iframe.contentWindow;
    const doc = iframe.contentDocument;
    if (!win || !doc) return;

    let printed = false;
    const trigger = () => {
      if (printed) return;
      printed = true;
      try {
        win.focus();
        win.print();
      } catch {
        /* ignore */
      }
      // Iframe opruimen na print (geef browser even tijd).
      setTimeout(() => iframe.remove(), 4000);
    };

    // Het REPOSITION_SCRIPT luistert op het load-event en herpositioneert
    // synchronisch daarna. Wacht op load, geef het script even de tijd, druk af.
    iframe.addEventListener("load", () => setTimeout(trigger, 300));
    // Fallback voor browsers waar het load-event op doc.write niet betrouwbaar is.
    setTimeout(trigger, 1500);

    // <base> injecteren direct na <head> zodat relatieve /uploads-URLs resolveert.
    const withBase = html.includes("<base ")
      ? html
      : html.replace(/<head>/i, `<head><base href="${API_URL}/">`);
    doc.open();
    doc.write(withBase);
    doc.close();
  }

  const openMail = () => {
    setMailError("");
    setMailOk("");
    setMailOpen(true);
  };

  const handleSendMail = async () => {
    setMailError("");
    setMailOk("");
    if (!to.trim()) {
      setMailError("Vul een e-mailadres in.");
      return;
    }
    setSending(true);
    try {
      const csv = buildCsvText(rows, columns);
      await api.post("/api/export/mail", { to, subject, csv, filename, source });
      setMailOk("E-mail verzonden.");
      setTimeout(() => setMailOpen(false), 1200);
    } catch (err: any) {
      const msg = err?.response?.data?.error;
      setMailError(
        msg === "SMTP niet geconfigureerd"
          ? "SMTP is niet geconfigureerd op de server. Vraag de beheerder om SMTP in te stellen."
          : msg || "Verzenden mislukt. Probeer het opnieuw."
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <div className="flex items-center gap-2">
        <Button variant="secondary" size="sm" onClick={handleCsv} title="Exporteer als CSV">
          <FileDown size={18} /> CSV
        </Button>
        <Button variant="secondary" size="sm" onClick={handleXlsx} title="Exporteer als Excel">
          <FileSpreadsheet size={18} /> Excel
        </Button>
        <Button variant="secondary" size="sm" onClick={handlePdf} loading={pdfBusy} title="Print / opslaan als PDF">
          <Printer size={18} /> PDF
        </Button>
        <Button variant="secondary" size="sm" onClick={openMail} title="E-mail als CSV-bijlage">
          <Mail size={18} /> Mail
        </Button>
      </div>

      <Modal
        isOpen={mailOpen}
        onClose={() => setMailOpen(false)}
        title="Export mailen"
        subtitle={`CSV van ${rows.length} ${source} als bijlage`}
        size="sm"
      >
        <div className="space-y-4">
          <Input label="Aan" type="email" value={to} onChange={(e) => setTo(e.target.value)} />
          <Input label="Onderwerp" value={subject} onChange={(e) => setSubject(e.target.value)} />

          {mailError && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
              {mailError}
            </p>
          )}
          {mailOk && (
            <p className="text-sm text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2">
              {mailOk}
            </p>
          )}

          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={() => setMailOpen(false)} disabled={sending}>
              Annuleren
            </Button>
            <Button onClick={handleSendMail} loading={sending}>
              Verzenden
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}